import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnFilterInput } from '../../filters/column/ColumnFilterInput';
import theme from '../../../theme/theme';
import type { SortOrder, TableTemplateProps } from './TableTemplateTypes';
import { TruncatedCell } from './TruncatedCell';

const LABEL = theme.palette.text.label;
const MUTED = theme.palette.action.disabled;
// Solid opaque background required for sticky column coverage (no transparency)
const HEADER_BG = theme.palette.grey[100];
// Zebra stripe for odd rows — a pale neutral grey (no blue tint). Opaque so
// sticky cells reuse it to cover scroll bleed.
const STRIPE_BG = 'rgb(243 243 243)';
// Row hover — a slightly darker neutral grey (overrides the zebra stripe + the
// default MUI action.hover so hover reads uniformly across normal & sticky cells).
const HOVER_BG = 'rgb(226 226 226)';

type ColumnHeaderArgs = {
  label: ReactNode;
  isActive: boolean;
  direction: SortOrder | undefined;
  onSort: () => void;
  baseColor: string;
};

const renderSortableHeader = ({
  label,
  isActive,
  direction,
  onSort,
  baseColor,
}: ColumnHeaderArgs): ReactNode => (
  <TableSortLabel
    active={isActive}
    direction={direction}
    onClick={onSort}
    sx={{
      fontWeight: theme.typography.fontWeightBold,
      fontSize: '0.75rem',
      color: isActive ? theme.palette.text.body : baseColor,
      '&.Mui-active': { color: theme.palette.text.body },
      '& .MuiTableSortLabel-icon': { color: `${theme.palette.text.body} !important` },
      '&:hover': { color: theme.palette.text.body },
      textTransform: 'none',
      minWidth: 0,
      overflow: 'hidden',
    }}
  >
    {label}
  </TableSortLabel>
);

const renderStaticHeader = ({ label }: ColumnHeaderArgs): ReactNode => label;

// ── Component ─────────────────────────────────────────────────────────────────

export function TableTemplate<T>({
  columns,
  rows = [],
  toolbar,
  toolbarActions,
  filterPanel,
  pagination,
  loading = false,
  maxHeight,
  fillHeight = false,
  sortState = null,
  onSortChange,
  filterState,
  onFilterChange,
  onFilterClear,
  emptyStateMessage,
  errorState,
  showEmptyStateIcon = true,
  showEmptyStateHint = true,
  emptyStateAlign = 'center',
  onRowClick,
  isRowSelected,
  hideHeader = false,
  stickyHeader = false,
  striped = false,
  collapsible = false,
  defaultExpanded = true,
}: TableTemplateProps<T>) {
  const { t } = useTranslation('common');
  const [expanded, setExpanded] = useState(defaultExpanded);

  const hasFilterableCols = columns.some(col => col.filter != null);

  /** Cumulative left offsets for sticky columns, keyed by column key. */
  const stickyMeta = useMemo(() => {
    const map = new Map<string | number | symbol, { left: number }>();
    let offset = 0;
    for (const col of columns) {
      if (col.sticky) {
        map.set(col.key, { left: offset });
        offset += typeof col.width === 'number' ? col.width : 0;
      }
    }
    return map;
  }, [columns]);

  /** Cumulative right offsets for sticky-right columns, keyed by column key. */
  const stickyRightMeta = useMemo(() => {
    const map = new Map<string | number | symbol, { right: number }>();
    let offset = 0;
    for (let i = columns.length - 1; i >= 0; i--) {
      const col = columns[i];
      if (col.stickyRight) {
        map.set(col.key, { right: offset });
        offset += typeof col.width === 'number' ? col.width : 0;
      }
    }
    return map;
  }, [columns]);

  // The scroll area is clipped to the card's radius so the grey header / striped
  // rows don't bleed square corners past the rounded Paper. Only round the corners
  // the scroll box actually forms: its top is a card edge when nothing sits above
  // it, its bottom when no pagination bar sits below it. `collapsible` renders a top
  // bar (chevron) even without a toolbar, so it also pushes the scroll box off the edge.
  const scrollTopIsCardEdge = !toolbar && !toolbarActions && !filterPanel && !collapsible;
  const scrollBottomIsCardEdge = !(pagination && rows.length > 0);
  // Nest inside the card: the Paper radius (theme token) minus its 1px border.
  const INNER_RADIUS = `calc(${theme.borderRadius.lg} - 1px)`;

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        borderRadius: theme.borderRadius.lg,
        border: `1px solid ${theme.palette.divider}`,
        // overflow: clip retiré — il peut casser position:sticky sur les colonnes
        // sticky-right/left dans certains moteurs de rendu. Le border-radius suffit
        // pour délimiter visuellement le Paper ; les cellules sticky restent dans
        // les bornes du scroll-container donc aucun débordement visible.
        ...(fillHeight && { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }),
        ...(maxHeight != null &&
          !fillHeight && { display: 'flex', flexDirection: 'column', maxHeight }),
      }}
    >
      {/* ── Top bar: toolbar + toolbarActions ──
          When collapsible, the whole bar toggles the fold (not just the chevron).
          toolbarActions stop propagation so their controls never fold the table;
          the chevron stays the accessible control (keyboard + aria-expanded). */}
      {(toolbar || toolbarActions || collapsible) && (
        <Box
          onClick={collapsible ? () => setExpanded(e => !e) : undefined}
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            ...(collapsible && { cursor: 'pointer', userSelect: 'none' }),
            ...(!filterPanel && { borderBottom: `1px solid ${theme.palette.divider}` }),
          }}
        >
          <Box>{toolbar}</Box>
          <Box
            onClick={collapsible ? e => e.stopPropagation() : undefined}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            {toolbarActions}
            {collapsible && (
              <IconButton
                size="small"
                onClick={() => setExpanded(e => !e)}
                aria-expanded={expanded}
                aria-label={expanded ? t('collapsibleCard.collapse') : t('collapsibleCard.expand')}
                title={expanded ? t('collapsibleCard.collapse') : t('collapsibleCard.expand')}
                sx={{ flexShrink: 0 }}
              >
                <ExpandMoreIcon
                  fontSize="small"
                  sx={{
                    transition: 'transform 0.2s',
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                  }}
                />
              </IconButton>
            )}
          </Box>
        </Box>
      )}
      {/* ── Filter panel ── */}
      {filterPanel}

      {/* ── Loading indicator appear on loading and disappear when finished loading ── */}
      <LinearProgress
        sx={{
          height: loading ? 3 : 0,
          transition: 'height 0.2s ease',
          '&.MuiLinearProgress-root': { backgroundColor: alpha(theme.palette.primary.main, 0.35) },
          '& .MuiLinearProgress-bar': {
            backgroundColor: alpha(theme.palette.primary.main, 0.35),
            transition: 'transform 0.4s linear',
          },
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      />

      {/* ── Table ── */}
      {(!collapsible || expanded) && (
        <Box
          sx={{
            width: '100%',
            overflowX: 'auto',
            // Clip the header/rows to the card radius (see flags above). Clipping on the
            // scroll container — not the Paper — keeps sticky columns/header working.
            borderTopLeftRadius: scrollTopIsCardEdge ? INNER_RADIUS : 0,
            borderTopRightRadius: scrollTopIsCardEdge ? INNER_RADIUS : 0,
            borderBottomLeftRadius: scrollBottomIsCardEdge ? INNER_RADIUS : 0,
            borderBottomRightRadius: scrollBottomIsCardEdge ? INNER_RADIUS : 0,
            ...(maxHeight != null && { flex: 1, minHeight: 0, overflowY: 'auto' }),
            ...(fillHeight && { flex: 1, minHeight: 0, overflowY: 'auto' }),
            opacity: loading ? 0.5 : 1,
            transition: 'opacity 0.2s ease',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        >
          <Table
            size="small"
            stickyHeader={stickyHeader}
            sx={{
              width: 'max-content',
              minWidth: '100%',
              tableLayout: 'auto',
              borderCollapse: 'separate',
              borderSpacing: 0,
            }}
          >
            {!hideHeader && (
              <TableHead>
                <TableRow
                  sx={{
                    '& .MuiTableCell-head': { borderBottom: `1px solid ${theme.palette.divider}` },
                  }}
                >
                  {columns.map(col => {
                    const isSortActive = sortState?.sortBy === col.key;
                    const isFilterActive = filterState?.[col.key] != null;
                    const baseHeaderColor = alpha(theme.palette.text.body, 0.8);
                    const labelColor = isFilterActive ? theme.palette.text.body : baseHeaderColor;
                    const labelText = t(col.labelKey);
                    const label = (
                      <TruncatedCell
                        text={labelText}
                        sx={{
                          fontWeight: theme.typography.fontWeightBold,
                          fontSize: '0.75rem',
                          color: labelColor,
                        }}
                      />
                    );
                    const stickyEntry = stickyMeta.get(col.key);
                    const stickyRightEntry = stickyRightMeta.get(col.key);
                    return (
                      <TableCell
                        key={String(col.key)}
                        align={col.align ?? 'left'}
                        //fixed the header background color for sticky columns to prevent bleed-through when scrolling
                        style={
                          stickyEntry || stickyRightEntry
                            ? { backgroundColor: HEADER_BG }
                            : undefined
                        }
                        sx={{
                          // Sticky columns need an exact fixed width so cumulative offsets stay accurate.
                          ...(col.sticky || col.stickyRight
                            ? { width: col.width, minWidth: col.width, maxWidth: col.width }
                            : fillHeight
                              ? {}
                              : { minWidth: col.width }),
                          py: 1.25,
                          verticalAlign: hasFilterableCols ? 'top' : 'middle',
                          backgroundColor: HEADER_BG,
                          ...(stickyEntry && {
                            position: 'sticky',
                            left: stickyEntry.left,
                            zIndex: 4,
                          }),
                          ...(stickyRightEntry && {
                            position: 'sticky',
                            right: stickyRightEntry.right,
                            zIndex: 4,
                          }),
                        }}
                        sortDirection={col.sortable && isSortActive ? sortState!.sortOrder : false}
                      >
                        <Box
                          sx={{
                            minHeight: '2.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            // Header label follows the column's alignment (right /
                            // center) instead of always hugging the left.
                            justifyContent:
                              col.align === 'right'
                                ? 'flex-end'
                                : col.align === 'center'
                                  ? 'center'
                                  : 'flex-start',
                            overflow: 'hidden',
                          }}
                        >
                          {(col.sortable ? renderSortableHeader : renderStaticHeader)({
                            label,
                            isActive: isSortActive,
                            direction: isSortActive ? sortState!.sortOrder : undefined,
                            onSort: () => onSortChange?.(col.key),
                            baseColor: baseHeaderColor,
                          })}
                        </Box>

                        {/* Inline column filter — always starts at the same vertical position */}
                        {col.filter != null && (
                          <Box sx={{ mt: 0.75 }}>
                            <ColumnFilterInput
                              def={col.filter}
                              value={filterState?.[col.key]}
                              onChange={v => onFilterChange?.(col.key, v)}
                              onClear={() => onFilterClear?.(col.key)}
                            />
                          </Box>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
            )}

            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} sx={{ border: 0 }}>
                    {errorState ?? (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: emptyStateAlign === 'left' ? 'flex-start' : 'center',
                          py: emptyStateAlign === 'left' ? 3 : 8,
                          px: emptyStateAlign === 'left' ? 2 : 0,
                          gap: 1,
                        }}
                      >
                        {showEmptyStateIcon && (
                          <Box
                            sx={{
                              width: 72,
                              height: 72,
                              borderRadius: '50%',
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 1,
                            }}
                          >
                            <ManageSearchIcon
                              sx={{ fontSize: 36, color: alpha(theme.palette.primary.main, 0.4) }}
                            />
                          </Box>
                        )}
                        <Typography
                          variant="body1"
                          fontWeight={theme.typography.fontWeightSemiBold}
                          sx={{ color: LABEL }}
                        >
                          {emptyStateMessage ?? t('table.noDataFound')}
                        </Typography>
                        {showEmptyStateHint && (
                          <Typography variant="body2" sx={{ color: MUTED }}>
                            {t('table.tryAdjusting')}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, rowIndex) => {
                  const selected = isRowSelected ? isRowSelected(row) : false;
                  // Zebra stripe every other row (opt-in), unless the row is selected.
                  const stripedRow = striped && !selected && rowIndex % 2 === 1;
                  return (
                    <TableRow
                      key={rowIndex}
                      hover
                      selected={selected}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      onKeyDown={
                        onRowClick
                          ? e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onRowClick(row);
                              }
                            }
                          : undefined
                      }
                      tabIndex={onRowClick ? 0 : undefined}
                      role={onRowClick ? 'button' : undefined}
                      sx={{
                        '&:last-child td': { border: 0 },
                        cursor: onRowClick ? 'pointer' : 'default',
                        ...(stripedRow && { backgroundColor: STRIPE_BG }),
                        '&:hover': { backgroundColor: HOVER_BG },
                        ...(selected && {
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          '&.Mui-selected': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                          '&.Mui-selected:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                          },
                        }),
                      }}
                    >
                      {columns.map(col => {
                        const stickyEntry = stickyMeta.get(col.key);
                        const stickyRightEntry = stickyRightMeta.get(col.key);
                        return (
                          <TableCell
                            key={String(col.key)}
                            align={col.align ?? 'left'}
                            style={
                              stickyEntry || stickyRightEntry
                                ? {
                                    backgroundColor: stripedRow
                                      ? STRIPE_BG
                                      : theme.palette.common.white,
                                  }
                                : undefined
                            }
                            sx={{
                              'tr:hover &': {
                                backgroundColor: `${HOVER_BG} !important`,
                              },
                              ...(col.width != null && {
                                maxWidth: col.width,
                                overflow: 'hidden',
                              }),
                              ...(stickyEntry && {
                                position: 'sticky',
                                left: stickyEntry.left,
                                zIndex: 3,
                              }),
                              ...(stickyRightEntry && {
                                position: 'sticky',
                                right: stickyRightEntry.right,
                                zIndex: 3,
                                width: col.width,
                                minWidth: col.width,
                                maxWidth: col.width,
                                boxShadow: '-8px 0 12px -8px rgba(0,0,0,0.05)',
                              }),
                            }}
                          >
                            {col.render ? (
                              col.render(row)
                            ) : (
                              <TruncatedCell
                                text={String(row[col.key] ?? '')}
                                sx={{ fontSize: '0.85rem', color: theme.palette.text.body }}
                              />
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* ── Pagination ── */}
      {/* Hidden when the table is empty so the empty state and the pager are
          mutually exclusive (never "0–0 of 0" under a "no data" message). */}
      {(!collapsible || expanded) && pagination && rows.length > 0 && (
        <>
          <Divider />
          {pagination}
        </>
      )}
    </Paper>
  );
}
